import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Cpu, Workflow, Wrench, BarChart3, Users, ShieldCheck } from 'lucide-react';
import PreviewBanner from './PreviewBanner';

const reasons = [
  { icon: Award,       title: 'Built from real-world delivery experience',  desc: 'Twenty years of running complex programmes — in hospitals, on construction sites, across PFI portfolios — distilled into every framework, process and product we ship.' },
  { icon: Cpu,         title: 'Proprietary digital platform — Videra',      desc: 'Our own platform, built and owned by us. No vendor lock-in to a third-party tool that doesn\'t understand your sector.' },
  { icon: Workflow,    title: 'Proven methodology',                          desc: 'A structured, seven-stage delivery framework refined across more than a thousand projects — repeatable, auditable, and tailored to your governance.' },
  { icon: Wrench,      title: 'Hands-on implementation',                     desc: 'We embed with your team to deploy systems, train people, and make change stick. No glossy report and disappearing act.' },
  { icon: BarChart3,   title: 'Measurable results',                          desc: 'Cycle times reduced, reporting accelerated, compliance evidence captured — outcomes you can demonstrate to your board, auditor, or regulator.' },
  { icon: Users,       title: 'People-first, culture-led approach',          desc: 'Technology only works when teams adopt it. We invest in training, mentoring and cultural alignment so new behaviours stick.' },
  { icon: ShieldCheck, title: 'Compliance built in',                         desc: 'CDM, H&S, Building Safety Act and statutory obligations embedded directly into workflows — so safe systems of work are consistent and easy to follow.' },
];

const PreviewAbout = () => (
  <div data-testid="preview-about">
    <PreviewBanner label="About" livePath="/about" liveLabel="About" />

    {/* HERO */}
    <section className="pt-16 pb-12 md:pt-24 md:pb-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">About us</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.05] max-w-4xl">
          Transform your project delivery.
        </h1>
        <div className="mt-8 space-y-5 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-3xl">
          <p>
            At <strong className="text-slate-900">Keystone Strategic Consultants</strong>, we help
            organisations solve complex operational, governance, and delivery challenges. Our work
            is powered by <strong className="text-slate-900">Videra</strong> — our proprietary
            digital platform designed to turn fragmented, inconsistent workflows into mapped,
            auditable, high-performance systems.
          </p>
          <p>
            We combine deep operational expertise with hands-on training, mentoring, and cultural
            alignment to ensure your teams adopt new ways of working with confidence. The result is
            transformation that doesn&apos;t just launch — it lasts.
          </p>
        </div>
      </div>
    </section>

    {/* WHY ORGANISATIONS CHOOSE KEYSTONE */}
    <section className="py-20 md:py-28 bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Why Keystone</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight">
            Why organisations choose Keystone.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
          {reasons.map((r, i) => (
            <div key={r.title} className={`flex items-start gap-5 py-7 ${i < reasons.length - 1 ? 'border-b border-slate-100' : ''}`}>
              <span className="w-11 h-11 rounded-lg bg-blue-50 border border-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                <r.icon className="w-5 h-5" />
              </span>
              <div>
                <h3 className="text-lg font-medium text-slate-900 leading-snug">{r.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{r.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* PRACTITIONER NARRATIVE */}
    <section className="py-20 md:py-28 bg-slate-900 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">Built by practitioners</p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
          We embed, <span className="text-blue-400">not advise from the sidelines.</span>
        </h2>
        <div className="mt-10 space-y-6 text-lg md:text-xl text-slate-300 leading-relaxed">
          <p>Too many consultancies deliver a report and disappear. We take a different approach — working alongside your teams to implement real change that sticks.</p>
          <p>Our frameworks are designed to become your frameworks. Our systems become your competitive advantage. When we leave, you&apos;re stronger than when we arrived.</p>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-24 md:py-32 bg-slate-950 text-white text-center">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">Ready to transform your delivery?</h2>
        <p className="mt-6 text-lg text-slate-400">Let&apos;s talk about how Keystone and Videra can elevate your operational performance.</p>
        <Link to="/contact" className="inline-flex items-center justify-center bg-white text-slate-900 px-8 py-4 font-medium tracking-wide hover:bg-slate-100 transition-all mt-10 rounded-md group">
          Speak to our team <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </section>
  </div>
);

export default PreviewAbout;
